import { useCallback, useEffect, useRef, useState } from "react";
import { FavouritesPanel, SaveFavouriteButton } from "./FavouritesPanel";

/** Bar length per MT5 timeframe code, in seconds. */
const TF_SECONDS: Record<string, number> = {
  M1: 60,
  M5: 300,
  M15: 900,
  M30: 1800,
  H1: 3600,
  H4: 14400,
  D1: 86400,
};
/** How many bars the convergence window spans by default. */
const CONVERGENCE_WINDOW_BARS = 3;
/** One strategy per family: trend, momentum, breakout. */
const CONVERGENCE_DEFAULT_SET = ["ema_trend", "rsi_momentum", "donchian_breakout"];
const CONVERGED_BRACKET_KEYS = new Set(["sl_pips", "tp_pips"]);

/** YYYY-MM-DD, `days` from today (UTC). */
function isoDate(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}

function windowHint(windowSec: number, timeframe: string): string {
  const secs = TF_SECONDS[timeframe];
  if (!secs) return "";
  const bars = windowSec / secs;
  return `~${Number.isInteger(bars) ? bars : bars.toFixed(1)} ${timeframe} bars`;
}
import {
  BacktestJobDetail,
  BacktestJobSummary,
  BacktestStrategySpec,
  BacktestSweepResult,
  BrokerSymbol,
  ConvergenceLeg,
  MarketDataSeries,
  PortfolioResult,
  RealismReport,
  StoreRange,
  ValidationResult,
  WalkForwardResult,
  api,
  isError,
} from "../api";

const RANK_OPTIONS = [
  "expectancy",
  "total_pnl",
  "return_pct",
  "profit_factor",
  "win_rate",
  "sharpe_annualised",
];

/** Transition Plan lever L14 slice 2 — run a price-replay backtest from the
 * dashboard. Upload an OHLCV CSV, pick a strategy + params, fire the job,
 * poll for the result, and render metrics + equity curve + trades.
 *
 * The job runs async on the backend (engine offloaded to a thread); this
 * panel polls GET /backtest/{id} until status leaves pending/running. */
export function BacktestPanel() {
  const [strategies, setStrategies] = useState<BacktestStrategySpec[]>([]);
  const [mode, setMode] = useState<
    "single" | "convergence" | "sweep" | "walkfwd" | "validate" | "favourites"
  >(
    "single",
  );
  const [strategyName, setStrategyName] = useState<string>("");
  const [params, setParams] = useState<Record<string, number>>({});
  // Sweep mode: each param holds a comma-separated list of values to scan.
  const [gridText, setGridText] = useState<Record<string, string>>({});
  const [rankBy, setRankBy] = useState("expectancy");
  const [sweep, setSweep] = useState<BacktestSweepResult | null>(null);
  // Walk-forward: reuses the sweep grid + rank, adds IS/OOS window sizes.
  const [isBars, setIsBars] = useState(500);
  const [oosBars, setOosBars] = useState(100);
  const [wfa, setWfa] = useState<WalkForwardResult | null>(null);
  const [holdoutPct, setHoldoutPct] = useState(20);
  const [portfolioIds, setPortfolioIds] = useState<number[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioResult | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  // Convergence mode: a list of sub-strategies that must all agree, plus
  // window + bracket (SL/TP live at config level, not per strategy).
  const [subs, setSubs] = useState<ConvergenceLeg[]>([]);
  // Window defaults to CONVERGENCE_WINDOW_BARS bars of the selected
  // timeframe (re-derived when the timeframe changes) - strategies signal
  // on bar close, so a 60 s window on M15 would demand same-bar agreement.
  const [windowSec, setWindowSec] = useState(CONVERGENCE_WINDOW_BARS * TF_SECONDS.M1);
  // 0 = strict convergence; > 1 = agreement boosts size instead (needs parity).
  const [boostMult, setBoostMult] = useState(0);
  // A converged entry is a confirmed-momentum entry: tighter stop, same 1:2.
  const [convSl, setConvSl] = useState(15);
  const [convTp, setConvTp] = useState(30);
  const [symbol, setSymbol] = useState("EURUSD");
  const [lot, setLot] = useState(0.1);
  // Live parity (gap G1): size by %-risk and apply the live risk gate and
  // exit rules, so a result measures the system that actually trades. On
  // by default; off reproduces the old fixed-lot mechanical replay.
  const [parity, setParity] = useState(true);
  const [balance, setBalance] = useState(10000);
  const [spread, setSpread] = useState(1.0);
  const [slippage, setSlippage] = useState(0.5);
  // Strategy plan Phase 0-1 cost realism: random slippage, slippage on
  // stops, wider spreads at rollover and around high-impact news. On by
  // default for new runs; the fields below set the news figures.
  const [realistic, setRealistic] = useState(true);
  const [newsSlippage, setNewsSlippage] = useState(2.5);
  const [newsSpreadMult, setNewsSpreadMult] = useState(3);
  const [rolloverSpreadMult, setRolloverSpreadMult] = useState(2);
  // The instrument's pip size + what a pip is worth per lot. Fetched from
  // the broker when the symbol changes: backtesting a JPY pair or a metal
  // with EURUSD's 0.0001 reports pips and P/L ~100x off, silently.
  const [pipSize, setPipSize] = useState(0.0001);
  const [pipValue, setPipValue] = useState(10.0);
  const [specSource, setSpecSource] = useState<"broker" | "fallback" | null>(null);
  const [file, setFile] = useState<File | null>(null);
  // Where the bars come from: an uploaded / MT5-fetched CSV, or a date
  // range of the stored price history (G11, Market data panel).
  const [dataSource, setDataSource] = useState<"file" | "store">("file");
  // Validate a basket of pairs together (backtest/basket.py): stored
  // history only, judged on the combined result.
  const [basket, setBasket] = useState(false);
  const [basketSymbols, setBasketSymbols] = useState(
    "EURUSD, GBPUSD, USDJPY, AUDUSD, USDCAD, USDCHF, NZDUSD, EURJPY, GBPJPY, EURGBP",
  );
  const [isYears, setIsYears] = useState(3);
  const [oosYears, setOosYears] = useState(1);
  const [storeStart, setStoreStart] = useState(() => isoDate(-730));
  const [storeEnd, setStoreEnd] = useState(() => isoDate(1));
  const [coverage, setCoverage] = useState<MarketDataSeries[]>([]);

  // Keep the fill model honest about the instrument. Runs on mount and
  // whenever the MT5 symbol changes; a manual CSV upload can still edit
  // the two fields by hand afterwards.
  const loadSpec = useCallback(async (sym: string) => {
    const r = await api.backtestSymbolSpec(sym);
    if (isError(r)) {
      setSpecSource(null);
      return;
    }
    setPipSize(r.spec.pip_size);
    setPipValue(r.spec.pip_value_per_lot);
    setSpecSource(r.spec.source);
  }, []);
  // MT5 history fetch (L14 slice 5): pulls bars via the bridge as a CSV
  // File that feeds the same run/sweep/walk-forward forms.
  const [mt5Symbol, setMt5Symbol] = useState("EURUSD");
  const [mt5Timeframe, setMt5TimeframeState] = useState("M1");
  function setMt5Timeframe(tf: string) {
    setMt5TimeframeState(tf);
    const secs = TF_SECONDS[tf];
    if (secs) setWindowSec(CONVERGENCE_WINDOW_BARS * secs);
  }
  // Timeframe label for the job. MT5 fetches know it; a manual CSV upload
  // does not, so it stays whatever the selector says.
  const jobTimeframe = mt5Timeframe;
  const [mt5Count, setMt5Count] = useState(5000);
  // Instruments offered in the fetch picker. Comes from the broker, so the
  // names already carry this account's suffixes; falls back to the majors
  // when the bridge is offline (flagged, so the UI can say so).
  const [symbols, setSymbols] = useState<BrokerSymbol[]>([]);
  const [symbolSource, setSymbolSource] = useState<"broker" | "fallback" | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [detail, setDetail] = useState<BacktestJobDetail | null>(null);
  const [recent, setRecent] = useState<BacktestJobSummary[]>([]);
  const pollRef = useRef<number | null>(null);

  // Load the strategy registry once; seed params from the first strategy.
  useEffect(() => {
    api.backtestStrategies().then((r) => {
      if (isError(r)) return;
      setStrategies(r.strategies);
      if (r.strategies.length > 0) {
        const first = r.strategies[0];
        setStrategyName(first.name);
        seedParams(first);
      }
    });
    refreshRecent();
    void loadSpec(mt5Symbol);
    void api.backtestSymbols().then((r) => {
      if (isError(r)) return;
      setSymbols(r.symbols);
      setSymbolSource(r.source);
    });
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function seedParams(spec: BacktestStrategySpec) {
    const seeded: Record<string, number> = {};
    const grid: Record<string, string> = {};
    for (const [key, p] of Object.entries(spec.params)) {
      seeded[key] = p.default;
      grid[key] = String(p.default); // sweep starts as the single default
    }
    setParams(seeded);
    setGridText(grid);
  }

  function defaultParams(name: string): Record<string, number> {
    const spec = strategies.find((s) => s.name === name);
    const seeded: Record<string, number> = {};
    if (spec) for (const [k, p] of Object.entries(spec.params)) seeded[k] = p.default;
    return seeded;
  }

  function addSub() {
    if (strategies.length === 0) return;
    const name = strategies[0].name;
    setSubs([...subs, { name, params: defaultParams(name) }]);
  }

  // First visit to the convergence tab: seed the recommended three-vote
  // set (one strategy per family) so the form is runnable as-is.
  useEffect(() => {
    if (mode !== "convergence" || subs.length > 0 || strategies.length === 0) return;
    const known = new Set(strategies.map((s) => s.name));
    const seed = CONVERGENCE_DEFAULT_SET.filter((n) => known.has(n));
    if (seed.length === 0) return;
    setSubs(seed.map((name) => ({ name, params: defaultParams(name) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, strategies]);

  function updateSub(idx: number, next: ConvergenceLeg) {
    setSubs(subs.map((s, i) => (i === idx ? next : s)));
  }

  function removeSub(idx: number) {
    setSubs(subs.filter((_, i) => i !== idx));
  }

  function onStrategyChange(name: string) {
    setStrategyName(name);
    const spec = strategies.find((s) => s.name === name);
    if (spec) seedParams(spec);
  }

  async function refreshRecent() {
    const r = await api.backtestResults();
    if (!isError(r)) setRecent(r.jobs);
  }

  async function onFetchMt5() {
    setBusy(true);
    setMessage(`Fetching ${mt5Count} ${mt5Timeframe} bars from MT5…`);
    await loadSpec(mt5Symbol);
    const r = await api.backtestMt5Bars(mt5Symbol, mt5Timeframe, mt5Count);
    setBusy(false);
    if (r instanceof File) {
      setFile(r);
      setSymbol(mt5Symbol);
      setMessage(`Fetched ${r.name} — ready to run.`);
    } else {
      setMessage(`MT5 fetch failed: ${r.error}`);
    }
  }

  const spec = strategies.find((s) => s.name === strategyName);

  /** The realism part of the fill model, as numbers (1 = on). */
  function realismFill(): Record<string, number> {
    if (!realistic) return {};
    return {
      slippage_random: 1,
      stop_slippage: 1,
      rollover_spread_mult: rolloverSpreadMult,
      news_spread_mult: newsSpreadMult,
      news_slippage_pips: newsSlippage,
      news_window_min: 5,
    };
  }

  /** The run's bars: the chosen CSV, or a stored range. Null (with a
   * message) when neither is ready. */
  function dataInput(): { file: File | null; store?: StoreRange } | null {
    if (dataSource === "store") {
      if (!storeStart || !storeEnd || storeEnd <= storeStart) {
        setMessage("Pick a stored range: the end date must be after the start.");
        return null;
      }
      return {
        file: null,
        store: { symbol, timeframe: jobTimeframe, start: storeStart, end: storeEnd },
      };
    }
    if (!file) {
      setMessage("Choose an OHLCV CSV first, or switch the data source to stored history.");
      return null;
    }
    return { file };
  }

  useEffect(() => {
    if (dataSource !== "store") return;
    void api.marketDataCoverage().then((r) => {
      if (!isError(r)) setCoverage(r.series);
    });
  }, [dataSource, symbol]);

  function parseGrid(): Record<string, number[]> {
    const grid: Record<string, number[]> = {};
    for (const [key, text] of Object.entries(gridText)) {
      const vals = text
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map(Number)
        .filter((v) => !Number.isNaN(v));
      if (vals.length > 0) grid[key] = vals;
    }
    return grid;
  }

  async function onRunSweep() {
    const input = dataInput();
    if (!input) return;
    setBusy(true);
    setMessage("Sweeping…");
    setSweep(null);
    setDetail(null);
    const r = await api.backtestSweep(input.file, {
      store: input.store,
      strategy: strategyName,
      grid: parseGrid(),
      rank_by: rankBy,
      fill: { spread_pips: spread, slippage_pips: slippage, pip_value_per_lot: 10.0, ...realismFill() },
      lot_size: lot,
      starting_balance: balance,
      parity,
    });
    setBusy(false);
    if (isError(r)) {
      setMessage(`Sweep failed: ${r.error}`);
      return;
    }
    setSweep(r);
    setMessage(`${r.combinations} combinations, ranked by ${r.rank_by}.`);
  }

  async function onRunWfa() {
    const input = dataInput();
    if (!input) return;
    setBusy(true);
    setMessage("Walking forward…");
    setWfa(null);
    setDetail(null);
    const r = await api.backtestWalkForward(input.file, {
      store: input.store,
      strategy: strategyName,
      grid: parseGrid(),
      is_bars: isBars,
      oos_bars: oosBars,
      rank_by: rankBy,
      fill: { spread_pips: spread, slippage_pips: slippage, pip_value_per_lot: 10.0, ...realismFill() },
      lot_size: lot,
      starting_balance: balance,
      parity,
    });
    setBusy(false);
    if (isError(r)) {
      setMessage(`Walk-forward failed: ${r.error}`);
      return;
    }
    setWfa(r);
    const eff = r.efficiency === null ? "n/a" : r.efficiency.toFixed(2);
    setMessage(`${r.n_folds} folds, efficiency ${eff}.`);
  }

  async function onRunBasket() {
    const symbols = basketSymbols
      .split(/[\s,]+/)
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean);
    if (symbols.length < 2) {
      setMessage("A basket needs at least two pairs.");
      return;
    }
    if (!storeStart || !storeEnd || storeEnd <= storeStart) {
      setMessage("Pick a stored range: a basket reads each pair's stored history.");
      return;
    }
    setBusy(true);
    setMessage(`Validating ${symbols.length} pairs together… this takes a while on years of data.`);
    setValidation(null);
    setDetail(null);
    const r = await api.backtestValidateBasket({
      strategy: strategyName,
      timeframe: jobTimeframe,
      symbols,
      store: { start: storeStart, end: storeEnd },
      grid: parseGrid(),
      is_days: Math.round(isYears * 365),
      oos_days: Math.round(oosYears * 365),
      rank_by: "sharpe_annualised",
      fill: { spread_pips: spread, slippage_pips: slippage, ...realismFill() },
      starting_balance: balance,
      parity,
      criteria: { holdout_frac: holdoutPct / 100 },
    });
    setBusy(false);
    if (isError(r)) {
      setMessage(`Basket validation failed: ${r.error}`);
      return;
    }
    setValidation(r);
    const failed = r.criteria.filter((c) => !c.passed).length;
    setMessage(
      r.verdict === "pass"
        ? `PASS on the basket; recorded for ${Object.keys(r.pair_validation_ids ?? {}).length} pairs.`
        : `ARCHIVED: ${failed} of ${r.criteria.length} criteria failed on the basket.`,
    );
  }

  async function onRunValidate() {
    if (basket) {
      await onRunBasket();
      return;
    }
    const input = dataInput();
    if (!input) return;
    setBusy(true);
    setMessage("Validating: sweep, walk-forward, stress, holdout, Monte Carlo…");
    setValidation(null);
    setDetail(null);
    const r = await api.backtestValidate(input.file, {
      store: input.store,
      strategy: strategyName,
      symbol,
      timeframe: jobTimeframe,
      grid: parseGrid(),
      is_bars: isBars,
      oos_bars: oosBars,
      rank_by: rankBy,
      fill: { spread_pips: spread, slippage_pips: slippage, pip_value_per_lot: pipValue,
              pip_size: pipSize, ...realismFill() },
      starting_balance: balance,
      parity,
      criteria: { holdout_frac: holdoutPct / 100 },
    });
    setBusy(false);
    if (isError(r)) {
      setMessage(`Validation failed: ${r.error}`);
      return;
    }
    setValidation(r);
    const failed = r.criteria.filter((c) => !c.passed).length;
    setMessage(
      r.verdict === "pass"
        ? "PASS: every criterion met."
        : `ARCHIVED: ${failed} of ${r.criteria.length} criteria failed.`,
    );
  }

  async function onCombine() {
    setBusy(true);
    setMessage("Combining runs…");
    const r = await api.backtestPortfolio(portfolioIds);
    setBusy(false);
    if (isError(r)) {
      setMessage(`Portfolio failed: ${r.error}`);
      return;
    }
    setPortfolio(r);
    setMessage(
      r.correlation.flagged.length
        ? `${r.correlation.flagged.length} correlated pair(s) flagged.`
        : "No pair of legs is strongly correlated.",
    );
  }

  async function onRun() {
    if (mode === "validate") {
      await onRunValidate();
      return;
    }
    if (mode === "sweep") {
      await onRunSweep();
      return;
    }
    if (mode === "walkfwd") {
      await onRunWfa();
      return;
    }
    const input = dataInput();
    if (!input) return;
    if (mode === "convergence" && subs.length === 0) {
      setMessage("Add at least one strategy for convergence mode.");
      return;
    }
    setBusy(true);
    setMessage("Uploading + queuing…");
    setDetail(null);
    const fill = {
      spread_pips: spread,
      slippage_pips: slippage,
      pip_size: pipSize,
      pip_value_per_lot: pipValue,
      ...realismFill(),
    };
    const config =
      mode === "convergence"
        ? {
            mode: "convergence" as const,
            symbol,
            timeframe: jobTimeframe,
            window_seconds: windowSec,
            boost_mult: boostMult,
            sl_pips: convSl,
            tp_pips: convTp,
            strategies: subs,
            fill,
            lot_size: lot,
            starting_balance: balance,
            parity,
          }
        : {
            symbol,
            timeframe: jobTimeframe,
            strategy: strategyName,
            params,
            fill,
            lot_size: lot,
            starting_balance: balance,
            parity,
          };
    const r = await api.backtestRun(input.file, { ...config, store: input.store });
    if (isError(r)) {
      setBusy(false);
      setMessage(`Failed: ${r.error}`);
      return;
    }
    setMessage(`Job #${r.job_id} running…`);
    poll(r.job_id);
  }

  function poll(jobId: number) {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      const d = await api.backtestJob(jobId);
      if (isError(d)) return;
      if (d.status === "completed" || d.status === "failed") {
        if (pollRef.current) window.clearInterval(pollRef.current);
        setBusy(false);
        setDetail(d);
        setMessage(
          d.status === "failed" ? `Job #${jobId} failed: ${d.error}` : null,
        );
        refreshRecent();
      }
    }, 500);
  }

  return (
    <section className="bg-surface border border-border rounded p-4 flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted">
          Backtest
          <span className="text-fg-muted normal-case tracking-normal ml-2 text-xs">
            (L14 slice 2 · price-replay engine)
          </span>
        </h2>
      </div>

      {/* ---- mode toggle (slice 3) ---- */}
      <div className="inline-flex rounded border border-border overflow-hidden text-xs w-fit">
        <button
          onClick={() => setMode("single")}
          className={
            "px-3 py-1 font-mono uppercase " +
            (mode === "single"
              ? "bg-success/20 text-success"
              : "bg-surface-2 text-fg-muted hover:text-fg")
          }
        >
          single
        </button>
        <button
          onClick={() => setMode("convergence")}
          className={
            "px-3 py-1 font-mono uppercase " +
            (mode === "convergence"
              ? "bg-success/20 text-success"
              : "bg-surface-2 text-fg-muted hover:text-fg")
          }
        >
          convergence
        </button>
        <button
          onClick={() => setMode("sweep")}
          className={
            "px-3 py-1 font-mono uppercase " +
            (mode === "sweep"
              ? "bg-success/20 text-success"
              : "bg-surface-2 text-fg-muted hover:text-fg")
          }
        >
          sweep
        </button>
        <button
          onClick={() => setMode("walkfwd")}
          className={
            "px-3 py-1 font-mono uppercase " +
            (mode === "walkfwd"
              ? "bg-success/20 text-success"
              : "bg-surface-2 text-fg-muted hover:text-fg")
          }
        >
          walk-fwd
        </button>
        <button
          onClick={() => setMode("validate")}
          className={
            "px-3 py-1 font-mono uppercase " +
            (mode === "validate"
              ? "bg-success/20 text-success"
              : "bg-surface-2 text-fg-muted hover:text-fg")
          }
        >
          validate
        </button>
        <button
          onClick={() => setMode("favourites")}
          className={
            "px-3 py-1 font-mono uppercase " +
            (mode === "favourites"
              ? "bg-success/20 text-success"
              : "bg-surface-2 text-fg-muted hover:text-fg")
          }
        >
          ★ favourites
        </button>
      </div>

      {mode === "favourites" && <FavouritesPanel />}

      {/* ---- config form ---- */}
      {mode !== "favourites" && (
      <>
      <div className="grid gap-3 lg:grid-cols-2 text-sm">
        <div className="flex flex-col gap-2">
          {mode === "single" ? (
            <>
              <label className="text-xs uppercase text-fg-muted">Strategy</label>
              <select
                value={strategyName}
                onChange={(e) => onStrategyChange(e.target.value)}
                className="bg-page border border-border rounded px-2 py-1 font-mono"
              >
                {strategies.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.label}
                  </option>
                ))}
              </select>
              {spec && <p className="text-xs text-fg-muted">{spec.description}</p>}
              {spec &&
                Object.entries(spec.params).map(([key, p]) => (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <label className="text-xs text-fg-muted">{p.label}</label>
                    <input
                      type="number"
                      value={params[key] ?? p.default}
                      min={p.min}
                      max={p.max}
                      step={p.kind === "int" ? 1 : 0.1}
                      onChange={(e) =>
                        setParams({ ...params, [key]: Number(e.target.value) })
                      }
                      className="bg-page border border-border rounded px-2 py-1 font-mono w-28 text-right"
                    />
                  </div>
                ))}
            </>
          ) : mode === "convergence" ? (
            <ConvergenceBuilder
              strategies={strategies}
              subs={subs}
              onAdd={addSub}
              onUpdate={updateSub}
              onRemove={removeSub}
              defaultParams={defaultParams}
              windowSec={windowSec}
              setWindowSec={setWindowSec}
              boostMult={boostMult}
              setBoostMult={setBoostMult}
              parity={parity}
              timeframe={mt5Timeframe}
              sl={convSl}
              setSl={setConvSl}
              tp={convTp}
              setTp={setConvTp}
            />
          ) : (
            <>
              <label className="text-xs uppercase text-fg-muted">Strategy</label>
              <select
                value={strategyName}
                onChange={(e) => onStrategyChange(e.target.value)}
                className="bg-page border border-border rounded px-2 py-1 font-mono"
              >
                {strategies.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-fg-muted">
                {mode === "validate"
                  ? "Each param takes a comma-separated list. The last holdout % of the bars is locked away; the rest is swept, the configuration is chosen from the broadest profitable plateau (not the single best cell), walk-forward tested, stress tested at 2x costs, run once on the holdout and resampled by Monte Carlo. The verdict is stored, and the live runner can be set to trade passing configurations only."
                  : mode === "walkfwd"
                  ? "Each param takes a comma-separated list. Each fold sweeps the in-sample window, then tests the winner on the out-of-sample window it never saw. Efficiency near 1.0 = the edge generalises."
                  : "Each param takes a comma-separated list of values to scan (e.g. 5,10,15). Every combination is run and ranked. Caveat: the winner is in-sample-fitted — validate with walk-forward before trusting it live."}
              </p>
              {spec &&
                Object.entries(spec.params).map(([key, p]) => (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <label className="text-xs text-fg-muted">{p.label}</label>
                    <input
                      type="text"
                      value={gridText[key] ?? String(p.default)}
                      onChange={(e) =>
                        setGridText({ ...gridText, [key]: e.target.value })
                      }
                      placeholder={`${p.default}`}
                      className="bg-page border border-border rounded px-2 py-1 font-mono w-40 text-right"
                    />
                  </div>
                ))}
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs text-fg-muted">Rank by</label>
                <select
                  value={rankBy}
                  onChange={(e) => setRankBy(e.target.value)}
                  className="bg-page border border-border rounded px-2 py-1 font-mono"
                >
                  {RANK_OPTIONS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
              {(mode === "walkfwd" || mode === "validate") && (
                <>
                  <NumberField
                    label="In-sample bars"
                    value={isBars}
                    onNumber={setIsBars}
                    step={50}
                  />
                  <NumberField
                    label="Out-of-sample bars"
                    value={oosBars}
                    onNumber={setOosBars}
                    step={25}
                  />
                  {mode === "validate" && (
                    <NumberField
                      label="Locked holdout (% of bars)"
                      value={holdoutPct}
                      onNumber={setHoldoutPct}
                      step={5}
                    />
                  )}
                  {mode === "validate" && (
                    <label
                      className="flex items-center gap-2 text-xs"
                      title="Run the strategy on every pair below and judge the COMBINED result - for portfolio strategies such as tsmom. Uses the stored-history date range."
                    >
                      <input
                        type="checkbox"
                        checked={basket}
                        onChange={(e) => setBasket(e.target.checked)}
                      />
                      Validate a basket of pairs together
                    </label>
                  )}
                  {mode === "validate" && basket && (
                    <>
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="uppercase text-fg-muted">Pairs (stored history)</span>
                        <textarea
                          value={basketSymbols}
                          onChange={(e) => setBasketSymbols(e.target.value)}
                          rows={2}
                          className="bg-page border border-border rounded px-2 py-1 font-mono"
                        />
                      </label>
                      <NumberField
                        label="Walk-forward in-sample (years)"
                        value={isYears}
                        onNumber={setIsYears}
                        step={1}
                      />
                      <NumberField
                        label="Walk-forward out-of-sample (years)"
                        value={oosYears}
                        onNumber={setOosYears}
                        step={1}
                      />
                      <p className="text-[10px] text-fg-muted">
                        Uses the From/To range of the stored-history data source and the timeframe
                        there (D1 for tsmom). Turn the weekend flatten off for a strategy that holds
                        through weekends.
                      </p>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <NumberField
            label={dataSource === "store" ? "Symbol" : "Symbol (label only)"}
            value={symbol}
            onText={setSymbol}
          />
          <label className="flex items-center gap-2 text-xs" title="Size by %-risk and apply the live risk gate, exit rules and daily-loss / weekend policies">
            <input
              type="checkbox"
              checked={parity}
              onChange={(e) => setParity(e.target.checked)}
            />
            Live parity (risk gate, %-risk sizing, live exits)
          </label>
          {parity ? (
            <p className="text-xs text-fg-muted">
              Lot size comes from Trading config (risk per trade); the field below is ignored.
            </p>
          ) : null}
          <NumberField label="Lot size" value={lot} onNumber={setLot} step={0.01} />
          <NumberField
            label="Starting balance"
            value={balance}
            onNumber={setBalance}
            step={100}
          />
          <NumberField label="Spread (pips)" value={spread} onNumber={setSpread} step={0.1} />
          <NumberField
            label="Pip size"
            value={pipSize}
            onNumber={setPipSize}
            step={0.0001}
          />
          <NumberField
            label="Pip value / lot"
            value={pipValue}
            onNumber={setPipValue}
            step={0.1}
          />
          {specSource && (
            <p className="text-[10px] text-fg-muted">
              {specSource === "broker"
                ? `pip size + value from the broker's ${symbol} contract`
                : "bridge offline — using your Trading-config fallbacks; check these before trusting the result"}
            </p>
          )}
          <NumberField
            label="Slippage (pips)"
            value={slippage}
            onNumber={setSlippage}
            step={0.1}
          />
          <label
            className="flex items-center gap-2 text-xs"
            title="Random slippage (0 to 2x the figure above), slippage on stops, wider spreads at rollover (21-23 UTC) and within 5 min of high-impact news"
          >
            <input
              type="checkbox"
              checked={realistic}
              onChange={(e) => setRealistic(e.target.checked)}
            />
            Realistic costs (random + stop slippage, rollover and news spreads)
          </label>
          {realistic && (
            <>
              <NumberField
                label="News slippage (pips)"
                value={newsSlippage}
                onNumber={setNewsSlippage}
                step={0.5}
              />
              <NumberField
                label="News spread (x usual)"
                value={newsSpreadMult}
                onNumber={setNewsSpreadMult}
                step={0.5}
              />
              <NumberField
                label="Rollover spread (x usual)"
                value={rolloverSpreadMult}
                onNumber={setRolloverSpreadMult}
                step={0.5}
              />
            </>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase text-fg-muted">Data source</label>
            <div className="flex gap-3 text-xs">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={dataSource === "file"}
                  onChange={() => setDataSource("file")}
                />
                CSV / MT5 fetch
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={dataSource === "store"}
                  onChange={() => setDataSource("store")}
                />
                Stored history
              </label>
            </div>
          </div>
          {dataSource === "store" ? (
            <StoredRange
              symbol={symbol}
              timeframe={mt5Timeframe}
              setTimeframe={setMt5Timeframe}
              start={storeStart}
              end={storeEnd}
              setStart={setStoreStart}
              setEnd={setStoreEnd}
              coverage={coverage}
            />
          ) : (
          <>
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase text-fg-muted">
              OHLCV CSV (timestamp,open,high,low,close)
            </label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-xs text-fg-muted"
            />
            {file && (
              <span className="text-[10px] text-fg-muted font-mono">
                using: {file.name}
              </span>
            )}
          </div>
          <Mt5Fetch
            symbol={mt5Symbol}
            setSymbol={setMt5Symbol}
            symbols={symbols}
            symbolSource={symbolSource}
            timeframe={mt5Timeframe}
            setTimeframe={setMt5Timeframe}
            count={mt5Count}
            setCount={setMt5Count}
            onFetch={onFetchMt5}
            busy={busy}
          />
          </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onRun}
          disabled={busy}
          className="px-3 py-2 rounded bg-success/20 border border-success text-success font-bold uppercase text-sm disabled:opacity-50"
        >
          {busy
            ? "running…"
            : mode === "sweep"
              ? "run sweep"
              : mode === "walkfwd"
                ? "run walk-forward"
                : mode === "validate"
                  ? "run validation"
                  : "run backtest"}
        </button>
        {message && <span className="text-xs font-mono text-fg-muted">{message}</span>}
      </div>
      </>
      )}

      {/* ---- result ---- */}
      {mode !== "favourites" && detail?.status === "completed" && detail.metrics && (
        <>
          <BacktestResultView detail={detail} />
          {detail.strategy_name !== "convergence" && (
            <SaveFavouriteButton
              jobId={detail.id}
              strategyName={detail.strategy_name}
            />
          )}
        </>
      )}

      {/* ---- sweep results ---- */}
      {sweep && <SweepResultView sweep={sweep} />}

      {/* ---- walk-forward results ---- */}
      {wfa && <WalkForwardResultView wfa={wfa} />}

      {/* ---- portfolio ---- */}
      {portfolio && <PortfolioView p={portfolio} />}

      {/* ---- validation verdict ---- */}
      {mode === "validate" && validation && <ValidationResultView v={validation} />}

      {/* ---- recent runs ---- */}
      {recent.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
            Recent runs
            {portfolioIds.length >= 2 && (
              <button
                onClick={() => void onCombine()}
                disabled={busy}
                className="ml-3 px-2 py-0.5 rounded border border-success text-success normal-case tracking-normal disabled:opacity-50"
              >
                combine {portfolioIds.length} as portfolio
              </button>
            )}
          </div>
          <table className="w-full text-xs font-mono">
            <thead className="text-fg-muted">
              <tr>
                <th className="text-left p-1" title="Select runs to combine into a portfolio">
                  pf
                </th>
                <th className="text-left p-1">#</th>
                <th className="text-left p-1">strategy</th>
                <th className="text-left p-1">status</th>
                <th className="text-right p-1">bars</th>
                <th className="text-right p-1">P/L</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((j) => (
                <tr
                  key={j.id}
                  className="cursor-pointer hover:bg-bg-hover"
                  onClick={() =>
                    api.backtestJob(j.id).then((d) => !isError(d) && setDetail(d))
                  }
                >
                  <td className="text-left p-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      disabled={j.status !== "completed"}
                      checked={portfolioIds.includes(j.id)}
                      onChange={(e) =>
                        setPortfolioIds(
                          e.target.checked
                            ? [...portfolioIds, j.id]
                            : portfolioIds.filter((x) => x !== j.id),
                        )
                      }
                    />
                  </td>
                  <td className="text-left p-1 text-fg-muted">{j.id}</td>
                  <td className="text-left p-1 text-fg-muted">{j.strategy_name}</td>
                  <td className="text-left p-1 text-fg-muted">{j.status}</td>
                  <td className="text-right p-1 text-fg-muted">{j.bars_count}</td>
                  <td
                    className={
                      "text-right p-1 " +
                      (j.total_pnl === null
                        ? "text-fg-muted"
                        : j.total_pnl >= 0
                          ? "text-success"
                          : "text-danger")
                    }
                  >
                    {j.total_pnl === null
                      ? "—"
                      : `${j.total_pnl >= 0 ? "+" : ""}${j.total_pnl.toFixed(2)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ConvergenceBuilder({
  strategies,
  subs,
  onAdd,
  onUpdate,
  onRemove,
  defaultParams,
  windowSec,
  timeframe,
  setWindowSec,
  boostMult,
  setBoostMult,
  parity,
  sl,
  setSl,
  tp,
  setTp,
}: {
  strategies: BacktestStrategySpec[];
  subs: ConvergenceLeg[];
  onAdd: () => void;
  onUpdate: (i: number, next: ConvergenceLeg) => void;
  onRemove: (i: number) => void;
  defaultParams: (name: string) => Record<string, number>;
  windowSec: number;
  timeframe: string;
  setWindowSec: (v: number) => void;
  boostMult: number;
  setBoostMult: (v: number) => void;
  parity: boolean;
  sl: number;
  setSl: (v: number) => void;
  tp: number;
  setTp: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-fg-muted">
        {boostMult > 1
          ? `Boost: every strategy below trades on its own; when two agree within the window the entry is sized at ${boostMult}x risk (or the open position topped up to it).`
          : "All strategies below must agree (same direction within the window) before a position opens — the live convergence rule."}{" "}
        SL/TP apply to every entry.
      </p>
      {subs.map((sub, idx) => {
        const spec = strategies.find((s) => s.name === sub.name);
        return (
          <div key={idx} className="border border-border rounded p-2 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <select
                value={sub.name}
                onChange={(e) =>
                  onUpdate(idx, { name: e.target.value, params: defaultParams(e.target.value) })
                }
                className="bg-page border border-border rounded px-2 py-1 font-mono flex-1"
              >
                {strategies.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select
                value={sub.timeframe ?? ""}
                title="Run this leg on a higher timeframe, built from the run's bars"
                onChange={(e) =>
                  onUpdate(idx, { ...sub, timeframe: e.target.value || undefined })
                }
                className="bg-page border border-border rounded px-1 py-1 font-mono text-xs"
              >
                <option value="">run tf</option>
                {["M5", "M15", "M30", "H1", "H4", "D1"].map((tf) => (
                  <option key={tf} value={tf}>
                    {tf}
                  </option>
                ))}
              </select>
              <button
                onClick={() => onRemove(idx)}
                className="px-2 py-1 rounded bg-surface-2 border border-border text-danger text-xs"
              >
                remove
              </button>
            </div>
            {spec &&
              Object.entries(spec.params)
                // The bracket is set once for the converged entry below;
                // per-strategy SL/TP are ignored in this mode.
                .filter(([key]) => !CONVERGED_BRACKET_KEYS.has(key))
                .map(([key, p]) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <label className="text-xs text-fg-muted">{p.label}</label>
                  <input
                    type="number"
                    value={sub.params[key] ?? p.default}
                    min={p.min}
                    max={p.max}
                    step={p.kind === "int" ? 1 : 0.1}
                    onChange={(e) =>
                      onUpdate(idx, {
                        ...sub,
                        params: { ...sub.params, [key]: Number(e.target.value) },
                      })
                    }
                    className="bg-page border border-border rounded px-2 py-1 font-mono w-28 text-right"
                  />
                </div>
              ))}
          </div>
        );
      })}
      <button
        onClick={onAdd}
        className="px-3 py-1 rounded bg-surface-2 border border-border text-fg text-xs uppercase w-fit"
      >
        + add strategy
      </button>
      <NumberField
        label={`Window (seconds) - ${windowHint(windowSec, timeframe)}`}
        value={windowSec}
        onNumber={setWindowSec}
        step={60}
      />
      <NumberField
        label="Agreement boost (x risk; 0 = strict)"
        value={boostMult}
        onNumber={setBoostMult}
        step={0.1}
      />
      {boostMult > 1 && !parity && (
        <p className="text-[10px] text-warn">Boost needs live parity (risk-based sizing) on.</p>
      )}
      <NumberField label="Stop-loss (pips), converged entry" value={sl} onNumber={setSl} step={1} />
      <NumberField label="Take-profit (pips), converged entry" value={tp} onNumber={setTp} step={1} />
    </div>
  );
}

/** Instrument picker: a dropdown of what the broker actually offers, with
 *  an escape hatch for anything not in the list (a symbol the trader has
 *  not added to Market Watch, or when the bridge is offline and the list
 *  is only the majors). */
function SymbolPicker({
  symbol,
  setSymbol,
  symbols,
  source,
}: {
  symbol: string;
  setSymbol: (v: string) => void;
  symbols: BrokerSymbol[];
  source: "broker" | "fallback" | null;
}) {
  const CUSTOM = "__custom__";
  const known = symbols.some((s) => s.name === symbol);
  // Typing mode sticks while the chosen symbol is not one the list knows,
  // so a hand-typed name is not wiped by a re-render.
  const [typing, setTyping] = useState(false);
  const custom = typing || (symbol !== "" && symbols.length > 0 && !known);

  if (custom) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="text"
          value={symbol}
          autoFocus={typing}
          onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          placeholder="SYMBOL"
          className="bg-page border border-border rounded px-2 py-1 font-mono w-28"
        />
        {symbols.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setTyping(false);
              setSymbol(symbols[0].name);
            }}
            title="back to the broker's list"
            className="px-1.5 py-1 rounded border border-border text-[10px] uppercase text-fg-muted hover:bg-bg-hover hover:text-fg"
          >
            list
          </button>
        )}
      </span>
    );
  }

  return (
    <select
      value={symbol}
      onChange={(e) => {
        if (e.target.value === CUSTOM) {
          setTyping(true);
          return;
        }
        setSymbol(e.target.value);
      }}
      title={
        source === "fallback"
          ? "bridge offline — showing the majors; pick Other to type any symbol"
          : "instruments from your broker's Market Watch"
      }
      className="bg-page border border-border rounded px-2 py-1 font-mono w-40"
    >
      {symbols.map((s) => (
        <option key={s.name} value={s.name}>
          {s.name}
          {s.description ? ` — ${s.description}` : ""}
        </option>
      ))}
      <option value={CUSTOM}>Other…</option>
    </select>
  );
}

function Mt5Fetch({
  symbol,
  setSymbol,
  symbols,
  symbolSource,
  timeframe,
  setTimeframe,
  count,
  setCount,
  onFetch,
  busy,
}: {
  symbol: string;
  setSymbol: (v: string) => void;
  symbols: BrokerSymbol[];
  symbolSource: "broker" | "fallback" | null;
  timeframe: string;
  setTimeframe: (v: string) => void;
  count: number;
  setCount: (v: number) => void;
  onFetch: () => void;
  busy: boolean;
}) {
  const TIMEFRAMES = ["M1", "M5", "M15", "M30", "H1", "H4", "D1"];
  return (
    <div className="flex flex-col gap-1 border-t border-border pt-2">
      <label className="text-xs uppercase text-fg-muted">
        …or fetch history from MT5 (needs the bridge connected)
      </label>
      <div className="flex items-center gap-2 flex-wrap">
        <SymbolPicker
          symbol={symbol}
          setSymbol={setSymbol}
          symbols={symbols}
          source={symbolSource}
        />
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          className="bg-page border border-border rounded px-2 py-1 font-mono"
        >
          {TIMEFRAMES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={count}
          min={2}
          step={500}
          onChange={(e) => setCount(Number(e.target.value))}
          className="bg-page border border-border rounded px-2 py-1 font-mono w-24 text-right"
        />
        <button
          onClick={onFetch}
          disabled={busy}
          className="px-3 py-1 rounded bg-surface-2 border border-border text-fg text-xs uppercase disabled:opacity-50"
        >
          fetch
        </button>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onNumber,
  onText,
  step,
}: {
  label: string;
  value: number | string;
  onNumber?: (v: number) => void;
  onText?: (v: string) => void;
  step?: number;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-xs text-fg-muted">{label}</label>
      <input
        type={onText ? "text" : "number"}
        value={value}
        step={step}
        onChange={(e) =>
          onText ? onText(e.target.value) : onNumber?.(Number(e.target.value))
        }
        className="bg-page border border-border rounded px-2 py-1 font-mono w-32 text-right"
      />
    </div>
  );
}

function BacktestResultView({ detail }: { detail: BacktestJobDetail }) {
  const m = detail.metrics!;
  return (
    <div className="flex flex-col gap-3">
      <EquityCurve points={detail.equity_curve} starting={detail.starting_balance} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
        <Metric label="Trades" value={`${m.trade_count}`} />
        <Metric label="Win rate" value={`${(m.win_rate * 100).toFixed(0)}%`} />
        <Metric
          label="Expectancy"
          value={`${m.expectancy >= 0 ? "+" : ""}${m.expectancy.toFixed(2)}`}
          good={m.expectancy >= 0}
        />
        <Metric
          label="Profit factor"
          value={m.profit_factor === null ? "∞" : m.profit_factor.toFixed(2)}
        />
        <Metric
          label="Total P/L"
          value={`${m.total_pnl >= 0 ? "+" : ""}${m.total_pnl.toFixed(2)}`}
          good={m.total_pnl >= 0}
        />
        <Metric label="Return" value={`${m.return_pct.toFixed(1)}%`} good={m.return_pct >= 0} />
        <Metric
          label="Max DD"
          value={`${m.max_drawdown_abs.toFixed(0)} (${(m.max_drawdown_pct * 100).toFixed(1)}%)`}
          good={false}
        />
        <Metric label="Sharpe" value={m.sharpe_annualised.toFixed(2)} />
        {m.expectancy_r !== undefined && m.expectancy_r !== null ? (
          <Metric
            label="Expectancy (R)"
            value={`${m.expectancy_r >= 0 ? "+" : ""}${m.expectancy_r.toFixed(2)}R`}
            good={m.expectancy_r >= 0}
          />
        ) : null}
        {m.trades_per_year ? (
          <Metric label="Trades / year" value={m.trades_per_year.toFixed(0)} />
        ) : null}
      </div>
      {m.parity?.enabled ? <ParitySummary parity={m.parity} /> : null}
      {m.realism ? <RealismNote r={m.realism} /> : null}
      {m.per_year && Object.keys(m.per_year).length > 1 ? (
        <PerYearTable perYear={m.per_year} />
      ) : null}
    </div>
  );
}

function ParitySummary({
  parity,
}: {
  parity: NonNullable<BacktestJobDetail["metrics"]>["parity"] & object;
}) {
  const rejected = Object.entries(parity.rejections);
  const events = Object.entries(parity.events);
  return (
    <div className="text-xs text-fg-muted">
      <span className="font-semibold text-fg">Live parity on.</span>{" "}
      {rejected.length === 0
        ? "No signals refused."
        : `Refused: ${rejected.map(([k, v]) => `${k} ${v}`).join(", ")}.`}{" "}
      {events.length > 0 ? `Events: ${events.map(([k, v]) => `${k} ${v}`).join(", ")}.` : null}
    </div>
  );
}

function PerYearTable({
  perYear,
}: {
  perYear: NonNullable<NonNullable<BacktestJobDetail["metrics"]>["per_year"]>;
}) {
  return (
    <table className="text-xs font-mono w-full">
      <thead>
        <tr className="text-fg-muted text-left">
          <th className="py-1">Year</th>
          <th className="text-right">Positions</th>
          <th className="text-right">P/L</th>
          <th className="text-right">Win rate</th>
          <th className="text-right">PF</th>
          <th className="text-right">Exp. R</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(perYear).map(([year, y]) => (
          <tr key={year} className="border-t border-border">
            <td className="py-1">{year}</td>
            <td className="text-right">{y.positions}</td>
            <td className="text-right">{y.total_pnl === null ? "-" : y.total_pnl.toFixed(2)}</td>
            <td className="text-right">
              {y.win_rate === null ? "-" : `${(y.win_rate * 100).toFixed(0)}%`}
            </td>
            <td className="text-right">
              {y.profit_factor === null ? "∞" : y.profit_factor.toFixed(2)}
            </td>
            <td className="text-right">
              {y.expectancy_r === null ? "-" : y.expectancy_r.toFixed(2)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SweepResultView({ sweep }: { sweep: BacktestSweepResult }) {
  if (sweep.results.length === 0) return null;
  const paramKeys = Object.keys(sweep.results[0].params);
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
        Sweep results — {sweep.combinations} combos, ranked by {sweep.rank_by}
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs font-mono">
          <thead className="text-fg-muted">
            <tr>
              <th className="text-left p-1">#</th>
              {paramKeys.map((k) => (
                <th key={k} className="text-right p-1">
                  {k}
                </th>
              ))}
              <th className="text-right p-1">trades</th>
              <th className="text-right p-1">win%</th>
              <th className="text-right p-1">expectancy</th>
              <th className="text-right p-1">PF</th>
              <th className="text-right p-1">total P/L</th>
            </tr>
          </thead>
          <tbody>
            {sweep.results.map((row, i) => (
              <tr key={i} className={i === 0 ? "bg-success/10" : ""}>
                <td className="text-left p-1 text-fg-muted">{i + 1}</td>
                {paramKeys.map((k) => (
                  <td key={k} className="text-right p-1 text-fg-muted">
                    {row.params[k]}
                  </td>
                ))}
                <td className="text-right p-1 text-fg-muted">{row.metrics.trade_count}</td>
                <td className="text-right p-1 text-fg-muted">
                  {(row.metrics.win_rate * 100).toFixed(0)}
                </td>
                <td
                  className={
                    "text-right p-1 " +
                    (row.metrics.expectancy >= 0 ? "text-success" : "text-danger")
                  }
                >
                  {row.metrics.expectancy >= 0 ? "+" : ""}
                  {row.metrics.expectancy.toFixed(2)}
                </td>
                <td className="text-right p-1 text-fg-muted">
                  {row.metrics.profit_factor === null
                    ? "∞"
                    : row.metrics.profit_factor.toFixed(2)}
                </td>
                <td
                  className={
                    "text-right p-1 " +
                    (row.metrics.total_pnl >= 0 ? "text-success" : "text-danger")
                  }
                >
                  {row.metrics.total_pnl >= 0 ? "+" : ""}
                  {row.metrics.total_pnl.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-fg-muted mt-1">
        Top row (green) is the in-sample winner — fitted to this data, so
        treat as a hypothesis, not a live setting.
      </p>
    </div>
  );
}

function WalkForwardResultView({ wfa }: { wfa: WalkForwardResult }) {
  const paramKeys = wfa.folds.length > 0 ? Object.keys(wfa.folds[0].best_params) : [];
  const eff = wfa.efficiency;
  const effColour =
    eff === null ? "text-fg-muted" : eff >= 0.7 ? "text-success" : eff >= 0.3 ? "text-warn" : "text-danger";
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs uppercase tracking-wider text-fg-muted">
        Walk-forward — {wfa.n_folds} folds, IS {wfa.is_bars} / OOS {wfa.oos_bars} bars
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
        <Metric
          label="WF efficiency"
          value={eff === null ? "n/a" : eff.toFixed(2)}
          good={eff !== null && eff >= 0.7}
        />
        <Metric label="OOS trades" value={`${wfa.combined_oos.trade_count}`} />
        <Metric
          label="OOS expectancy"
          value={`${wfa.combined_oos.expectancy >= 0 ? "+" : ""}${wfa.combined_oos.expectancy.toFixed(2)}`}
          good={wfa.combined_oos.expectancy >= 0}
        />
        <Metric
          label="OOS total P/L"
          value={`${wfa.combined_oos.total_pnl >= 0 ? "+" : ""}${wfa.combined_oos.total_pnl.toFixed(2)}`}
          good={wfa.combined_oos.total_pnl >= 0}
        />
      </div>
      <p className={`text-[10px] ${effColour}`}>
        {eff === null
          ? "Efficiency unavailable (near-zero in-sample expectancy)."
          : eff >= 0.7
            ? "Edge largely survives out-of-sample — generalises well."
            : eff >= 0.3
              ? "Partial decay out-of-sample — some overfitting."
              : "Edge collapses out-of-sample — the sweep was fitting noise."}
      </p>
      <div className="overflow-x-auto">
        <table className="text-xs font-mono">
          <thead className="text-fg-muted">
            <tr>
              <th className="text-left p-1">fold</th>
              {paramKeys.map((k) => (
                <th key={k} className="text-right p-1">
                  IS {k}
                </th>
              ))}
              <th className="text-right p-1">IS exp</th>
              <th className="text-right p-1">OOS trades</th>
              <th className="text-right p-1">OOS exp</th>
              <th className="text-right p-1">OOS P/L</th>
            </tr>
          </thead>
          <tbody>
            {wfa.folds.map((f) => (
              <tr key={f.fold_index}>
                <td className="text-left p-1 text-fg-muted">{f.fold_index}</td>
                {paramKeys.map((k) => (
                  <td key={k} className="text-right p-1 text-fg-muted">
                    {f.best_params[k]}
                  </td>
                ))}
                <td className="text-right p-1 text-fg-muted">
                  {f.is_expectancy.toFixed(2)}
                </td>
                <td className="text-right p-1 text-fg-muted">{f.oos.trade_count}</td>
                <td
                  className={"text-right p-1 " + (f.oos.expectancy >= 0 ? "text-success" : "text-danger")}
                >
                  {f.oos.expectancy >= 0 ? "+" : ""}
                  {f.oos.expectancy.toFixed(2)}
                </td>
                <td
                  className={"text-right p-1 " + (f.oos.total_pnl >= 0 ? "text-success" : "text-danger")}
                >
                  {f.oos.total_pnl >= 0 ? "+" : ""}
                  {f.oos.total_pnl.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, good }: { label: string; value: string; good?: boolean }) {
  const colour = good === undefined ? "text-fg" : good ? "text-success" : "text-danger";
  return (
    <div className="bg-page border border-border rounded p-2">
      <div className="text-[10px] uppercase text-fg-muted">{label}</div>
      <div className={`text-sm ${colour}`}>{value}</div>
    </div>
  );
}

/** Minimal dependency-free equity-curve sparkline as an inline SVG. */
function EquityCurve({
  points,
  starting,
}: {
  points: { ts_us: number; equity: number }[];
  starting: number;
}) {
  if (points.length < 2) return null;
  const W = 600;
  const H = 120;
  const equities = points.map((p) => p.equity);
  const min = Math.min(...equities, starting);
  const max = Math.max(...equities, starting);
  const range = max - min || 1;
  const x = (i: number) => (i / (points.length - 1)) * W;
  const y = (v: number) => H - ((v - min) / range) * H;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.equity).toFixed(1)}`).join(" ");
  const baseY = y(starting);
  const ending = equities[equities.length - 1];
  const up = ending >= starting;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
        Equity curve
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full border border-border rounded bg-page"
        preserveAspectRatio="none"
        height={H}
      >
        <line
          x1={0}
          y1={baseY}
          x2={W}
          y2={baseY}
          stroke="#475569"
          strokeWidth={1}
          strokeDasharray="4 4"
        />
        <path
          d={path}
          fill="none"
          stroke={up ? "#22c55e" : "#ef4444"}
          strokeWidth={1.5}
        />
      </svg>
    </div>
  );
}


/** One line on what the run's realistic costs and M1 exit resolution
 *  actually used, so a result says when they had no data to work with. */
function RealismNote({ r }: { r: RealismReport }) {
  const ib = r.intrabar;
  const parts: string[] = [];
  if (ib) {
    if (ib.m1_bars > 0) {
      const tp = ib.ambiguous_bars_resolved_tp ?? 0;
      const sl = ib.ambiguous_bars_resolved_sl ?? 0;
      const none = ib.ambiguous_bars_no_m1 ?? 0;
      parts.push(
        `M1 exits: ${ib.m1_bars.toLocaleString()} M1 bars; bars touching both stop and target: ${tp} target first, ${sl} stop first, ${none} without M1 (stop assumed)`,
      );
    } else {
      parts.push(ib.note ?? "no stored M1: stop assumed first");
    }
  }
  if (r.costs) parts.push(`realistic costs on${r.news_events !== undefined ? `, ${r.news_events} high-impact events` : ""}`);
  return (
    <div className="text-[10px] font-mono text-fg-muted flex flex-col">
      {parts.map((p) => (
        <span key={p}>{p}</span>
      ))}
      {r.news_warning && <span className="text-warn">{r.news_warning}</span>}
    </div>
  );
}

function ValidationResultView({ v }: { v: ValidationResult }) {
  const pass = v.verdict === "pass";
  const mc = v.report?.monte_carlo;
  const news = v.report?.news_coverage;
  const realismReport = v.report?.realism;
  return (
    <div className="flex flex-col gap-2 text-xs">
      {realismReport && Object.keys(realismReport).length > 0 && <RealismNote r={realismReport} />}
      <div className={"text-sm font-bold " + (pass ? "text-success" : "text-danger")}>
        {pass ? "PASS" : "ARCHIVED"} · {v.strategy} {v.symbol} {v.timeframe} · validation #{v.id}
      </div>
      <div className="font-mono text-fg-muted">
        Chosen: {Object.entries(v.params).map(([k, x]) => `${k}=${x}`).join(", ")}
      </div>
      <table className="font-mono w-full">
        <thead>
          <tr className="text-fg-muted text-left">
            <th className="py-1">Criterion</th>
            <th className="text-right">Value</th>
            <th className="text-right">Needs</th>
            <th className="text-right">Result</th>
            <th className="pl-3">Detail</th>
          </tr>
        </thead>
        <tbody>
          {v.criteria.map((c) => (
            <tr key={c.name} className="border-t border-border">
              <td className="py-1">{c.name.replace(/_/g, " ")}</td>
              <td className="text-right">{c.value === null ? "-" : c.value}</td>
              <td className="text-right">
                {c.name === "monte_carlo_drawdown_p95" ? "≤ " : "≥ "}
                {c.threshold}
              </td>
              <td className={"text-right " + (c.passed ? "text-success" : "text-danger")}>
                {c.passed ? "pass" : "fail"}
              </td>
              <td className="pl-3 text-fg-muted">{c.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {v.report?.per_pair && (
        <table className="font-mono w-full">
          <thead>
            <tr className="text-fg-muted text-left">
              <th className="py-1">Pair (development)</th>
              <th className="text-right">Trades</th>
              <th className="text-right">P/L</th>
              <th className="text-right">PF</th>
              <th className="text-right">Holdout P/L</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(v.report.per_pair).map(([sym, m]) => (
              <tr key={sym} className="border-t border-border">
                <td className="py-1">{sym}</td>
                <td className="text-right">{m.trade_count}</td>
                <td className={"text-right " + (m.total_pnl >= 0 ? "text-success" : "text-danger")}>
                  {m.total_pnl.toFixed(2)}
                </td>
                <td className="text-right">{m.profit_factor ?? "∞"}</td>
                <td className="text-right">
                  {v.report?.holdout_per_pair?.[sym]?.toFixed(2) ?? "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(v.report?.correlation?.flagged?.length ?? 0) > 0 && (
        <div className="text-warn">
          Strongly correlated pairs (monthly P/L):{" "}
          {v.report!.correlation!.flagged.map((f) => `${f.a}/${f.b} ${f.correlation}`).join(", ")}
        </div>
      )}
      {(v.report?.warnings ?? []).map((w) => (
        <div key={w} className="text-warn">
          {w}
        </div>
      ))}
      {mc && mc.runs ? (
        <div className="text-fg-muted">
          Monte Carlo ({mc.runs} runs): max drawdown median{" "}
          {(mc.max_drawdown_p50 * 100).toFixed(1)}%, 95th percentile{" "}
          {(mc.max_drawdown_p95 * 100).toFixed(1)}%; chance of a losing run{" "}
          {(mc.prob_loss * 100).toFixed(1)}%.
        </div>
      ) : null}
      {news ? (
        <div className={news.covered_fraction < 0.9 ? "text-warn" : "text-fg-muted"}>
          News calendar covered {(news.covered_fraction * 100).toFixed(0)}% of the bars (
          {news.events} events).
          {news.covered_fraction < 0.9
            ? " Outside it the news gate saw nothing: import a historical calendar (News → import) for a full test."
            : ""}
        </div>
      ) : null}
    </div>
  );
}


function PortfolioView({ p }: { p: PortfolioResult }) {
  const c = p.combined;
  const cell = (v: number | null) => (v === null ? "-" : v.toFixed(2));
  return (
    <div className="flex flex-col gap-2 text-xs">
      <div className="text-sm font-bold">Portfolio of {p.legs.length} runs</div>
      <div className="font-mono text-fg-muted">
        Combined P/L {c.total_pnl.toFixed(2)} ({c.return_pct ?? "-"}%), max drawdown{" "}
        {(c.max_drawdown_pct * 100).toFixed(1)}%
        {c.drawdown_vs_sum_of_legs !== null &&
          ` = ${(c.drawdown_vs_sum_of_legs * 100).toFixed(0)}% of the legs' drawdowns added up`}
        , {p.months} months.
      </div>
      <table className="font-mono w-full">
        <thead>
          <tr className="text-fg-muted text-left">
            <th className="py-1">Leg</th>
            <th className="text-right">Trades</th>
            <th className="text-right">P/L</th>
            <th className="text-right">PF</th>
            <th className="text-right">Max DD</th>
          </tr>
        </thead>
        <tbody>
          {p.legs.map((l) => (
            <tr key={l.label} className="border-t border-border">
              <td className="py-1">{l.label}</td>
              <td className="text-right">{l.trades}</td>
              <td className="text-right">{l.total_pnl.toFixed(2)}</td>
              <td className="text-right">{cell(l.profit_factor)}</td>
              <td className="text-right">{(l.max_drawdown_pct * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-fg-muted">Monthly P/L correlation</div>
      <table className="font-mono">
        <tbody>
          {p.correlation.matrix.map((row, i) => (
            <tr key={i}>
              <td className="pr-2 text-fg-muted">#{i + 1}</td>
              {row.map((v, j) => (
                <td
                  key={j}
                  className={
                    "px-2 text-right " +
                    (i !== j && v !== null && v > p.correlation.flag_above ? "text-warn" : "")
                  }
                >
                  {v === null ? "-" : v.toFixed(2)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {p.correlation.flagged.map((f) => (
        <div key={f.a + f.b} className="text-warn">
          {f.a} and {f.b} move together ({f.correlation}): one bet sized twice. Drop or
          down-weight one.
        </div>
      ))}
      <div className="text-fg-muted">{p.limitations}</div>
    </div>
  );
}

/** Date-range picker for backtesting from the stored price history. Shows
 * what is stored for the symbol, so an empty range is obvious up front. */
function StoredRange({
  symbol,
  timeframe,
  setTimeframe,
  start,
  end,
  setStart,
  setEnd,
  coverage,
}: {
  symbol: string;
  timeframe: string;
  setTimeframe: (tf: string) => void;
  start: string;
  end: string;
  setStart: (v: string) => void;
  setEnd: (v: string) => void;
  coverage: MarketDataSeries[];
}) {
  const mine = coverage.filter((c) => c.symbol === symbol.trim().toUpperCase());
  const want = TF_SECONDS[timeframe] ?? 0;
  const usable = mine.filter((c) => {
    const secs = TF_SECONDS[c.timeframe];
    return secs !== undefined && want % secs === 0;
  });
  return (
    <div className="flex flex-col gap-2 text-xs">
      <label className="flex flex-col gap-1">
        <span className="uppercase text-fg-muted">Timeframe</span>
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          className="bg-page border border-border rounded px-2 py-1 font-mono"
        >
          {Object.keys(TF_SECONDS).map((tf) => (
            <option key={tf} value={tf}>
              {tf}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-2">
        <label className="flex flex-col gap-1 flex-1">
          <span className="uppercase text-fg-muted">From</span>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="bg-page border border-border rounded px-2 py-1 font-mono"
          />
        </label>
        <label className="flex flex-col gap-1 flex-1">
          <span className="uppercase text-fg-muted">To (exclusive)</span>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="bg-page border border-border rounded px-2 py-1 font-mono"
          />
        </label>
      </div>
      {mine.length === 0 ? (
        <p className="text-[10px] text-warn">
          Nothing stored for {symbol || "this symbol"}. Fill or import it in Market data below.
        </p>
      ) : (
        <ul className="text-[10px] text-fg-muted font-mono">
          {mine.map((c) => (
            <li key={c.timeframe}>
              {c.timeframe}: {c.first?.slice(0, 10)} → {c.last?.slice(0, 10)} (
              {c.bars.toLocaleString()} bars)
              {usable.some((u) => u.timeframe === c.timeframe) && c.timeframe !== timeframe
                ? ` · builds ${timeframe}`
                : ""}
            </li>
          ))}
        </ul>
      )}
      {mine.length > 0 && usable.length === 0 && (
        <p className="text-[10px] text-warn">
          No stored timeframe divides {timeframe}; pick a higher timeframe or fill a lower one.
        </p>
      )}
    </div>
  );
}
